import type { TeamSubscription } from '@/lib/types'
import type { BillingCycle } from './queries'

// What Stripe charges (STRIPE_PRICE_PREMIUM_* in the API). The plan models in
// the API still carry placeholder USD prices, so the UI keeps its own, as v1 did.
export const PRICES: Record<BillingCycle, { amount: number; currency: string }> = {
  monthly: { amount: 199, currency: 'SEK' },
  yearly: { amount: 2189, currency: 'SEK' },
}

export const YEARLY_SAVING = Math.round((1 - PRICES.yearly.amount / (PRICES.monthly.amount * 12)) * 100)

/** i18n keys; `free` marks what the free plan includes. */
export const PLAN_FEATURES = [
  { key: 'addCompanies', free: true },
  { key: 'editCompanies', free: true },
  { key: 'importXls', free: true },
  { key: 'cityView', free: true },
  { key: 'hubspot', free: false },
  { key: 'zapier', free: false },
  { key: 'customFields', free: false },
] as const

export const cycleOf = (planId?: string | null): BillingCycle => (planId === 'premium_yearly' ? 'yearly' : 'monthly')
export const isPremiumPlan = (planId?: string | null) => planId === 'premium_monthly' || planId === 'premium_yearly'

const DAY = 24 * 60 * 60 * 1000

/** How long after a trial ends the app still says so. */
const TRIAL_ENDED_SHOWN_FOR = 14 * DAY

export type TrialState =
  | { state: 'none' }
  | { state: 'active'; ends: Date; daysLeft: number }
  | { state: 'ended'; ends: Date }

/**
 * Where the team's Premium trial stands. The api ends it by date alone (the
 * row stays, source "trial"), so a past expired_at is a trial that ended;
 * buying replaces the row and there is no trial any more.
 */
export function trialState(sub: TeamSubscription | null | undefined, now = Date.now()): TrialState {
  if (sub?.source !== 'trial' || !sub.expired_at) return { state: 'none' }
  const ends = new Date(sub.expired_at)
  const left = ends.getTime() - now
  if (left > 0) return { state: 'active', ends, daysLeft: Math.ceil(left / DAY) }
  return -left < TRIAL_ENDED_SHOWN_FOR ? { state: 'ended', ends } : { state: 'none' }
}

export const isTrialActive = (sub: TeamSubscription | null | undefined) => trialState(sub).state === 'active'
